import React, { FC, useState } from "react";
import { Box, HStack, Text, Flex, Button, keyframes } from "@chakra-ui/react";
import { Link } from "src/atoms/PrismicLink";
import { ShiIcon } from "src/atoms";
import ShiButton from "src/atoms/ShiButton";
import { MegaMenuProps } from "../types";

const slideDown = keyframes`
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
`;
const slideRightToLeft = keyframes`
  from { opacity: 0; transform: translateX(60px); }
  to { opacity: 1; transform: translateX(0); }
`;
const slideLeftToRight = keyframes`
  from { opacity: 0; transform: translateX(-60px); }
  to { opacity: 1; transform: translateX(0); }
`;

export const MegaMenu: FC<MegaMenuProps> = ({ body }) => {
  const [hoveredMenu, setHoveredMenu] = useState<number | null>(null);
  const [previousMenu, setPreviousMenu] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<number>(0);

  if (!body) return null;

  return (
    <HStack spacing="0" h="100%">
      {body.map((slice, index) => {
        const isHovered = hoveredMenu === index;
        const hasDropdown = slice.items?.some(
          (item: any) => item.header_fragment?.document?.data,
        );
        const showLeftTabs =
          hasDropdown &&
          slice.primary?.has_sub_menus === true &&
          slice.items &&
          slice.items.length > 1;

        let animation = `${slideDown} 0.2s ease-out forwards`;
        if (previousMenu !== null && previousMenu !== index) {
          if (index > previousMenu) {
            animation = `${slideRightToLeft} 0.2s ease-out forwards`;
          } else {
            animation = `${slideLeftToRight} 0.2s ease-out forwards`;
          }
        }

        return (
          <Box
            key={index}
            h="100%"
            display="flex"
            alignItems="center"
            onMouseEnter={() => {
              setPreviousMenu(hoveredMenu);
              setHoveredMenu(index);
              setActiveTab(0);
            }}
            onMouseLeave={() => {
              setPreviousMenu(null);
              setHoveredMenu(null);
            }}
            cursor={hasDropdown ? "default" : "pointer"}
          >
            {/* Top Level Item */}
            {hasDropdown ? (
              <Button
                variant="link-navbar"
                px="space-10"
                size="sm"
                whiteSpace="nowrap"
                isActive={isHovered}
              >
                {slice.primary?.menu_title}
                <ShiIcon
                  name="chevron"
                  boxSize="16px"
                  ml="1.5"
                  transform={isHovered ? "rotate(180deg)" : "rotate(0deg)"}
                  transition="transform 0.2s"
                />
              </Button>
            ) : (
              <Button
                as={Link}
                href={slice.primary?.link?.url}
                variant="link-navbar"
                px="space-10"
                size="sm"
                whiteSpace="nowrap"
                _hover={{ textDecoration: "none" }}
              >
                {slice.primary?.menu_title}
              </Button>
            )}

            {/* Dropdown Panel */}
            {hasDropdown && isHovered && (
              <Box
                position="absolute"
                top="100%"
                left="50%"
                transform="translateX(-50%)"
                zIndex="dropdown"
                mt="-1px"
              >
                {/* 
                  PROPORTIONAL SCALING STRATEGY
                   This scaling strategy was introduced specifically to handle the viewport before reaching the 
                  tablet breakpoint where the massive Mega Menu dropdown was previously being clipped off.
                  
                  To make sure the Mega Menu dropdown properly fits on smaller screens (e.g. 1024px to 1280px) 
                  without clipping off the edges, we use a fluid proportional scaling approach:
                  1. The base font-size of this container shrinks linearly based on viewport width below 1280px (1.25vw). 
                     At 1280px+, it stays locked to 16px.
                  2. All internal paddings, gaps, and widths use `em` instead of `px` or Chakra space tokens. 
                     Because `em` is relative to the parent's font-size, the entire panel shrinks down uniformly 
                     like a vector graphic as the screen gets smaller!
                  CRITICAL: Do NOT use hardcoded `px` or Chakra `space-*` tokens inside this dropdown, 
                  otherwise those elements will not scale down and the panel will overflow the screen. 
                */}
                <Box
                  fontSize={{ base: "min(16px, 1.25vw)", xl: "16px" }}
                  bg="white"
                  boxShadow="0px 10px 30px rgba(0, 0, 0, 0.1)"
                  borderRadius="md"
                  borderTopRadius="none"
                  w="max-content"
                  pt="2.5em"
                  pr="2.5em"
                  pb="3em"
                  pl="2.5em"
                >
                  <Flex animation={animation} willChange="transform, opacity">
                    {/* Left Column - Sub Menu Tabs */}
                    {showLeftTabs && (
                      <Box w="19.5625em" mr="4em" flexShrink={0}>
                        <Flex direction="column" gap="0.5em">
                          {slice.items?.map((item: any, i: number) => {
                            const isActive = activeTab === i;
                            return (
                              <Box
                                key={i}
                                cursor="pointer"
                                w="100%"
                                textAlign="left"
                                bg={
                                  isActive ? "btn-green-bg" : "monochrome-100"
                                }
                                color={isActive ? "white" : "monochrome-950"}
                                onMouseEnter={() => setActiveTab(i)}
                                px="1em"
                                py="0.75em"
                                borderRadius="md"
                                transition="background-color 0.2s, color 0.2s"
                              >
                                <Text
                                  fontWeight="medium"
                                  fontSize="1.125em"
                                  noOfLines={1}
                                  color={
                                    isActive
                                      ? "monochrome-100"
                                      : "monochrome-950"
                                  }
                                >
                                  {item.sub_menu_label}
                                </Text>
                                {item.sub_menu_description && (
                                  <Text
                                    fontSize="0.875em"
                                    mt="space-2"
                                    fontWeight="medium"
                                    noOfLines={1}
                                    color={
                                      isActive
                                        ? "monochrome-200"
                                        : "monochrome-600"
                                    }
                                    lineHeight="short"
                                  >
                                    {item.sub_menu_description}
                                  </Text>
                                )}
                              </Box>
                            );
                          })}
                        </Flex>
                      </Box>
                    )}

                    {/* Right Column - Active Content (Grid Overlay) */}
                    <Box
                      display="grid"
                      flexGrow={1}
                      gridTemplateColumns="1fr"
                      gridTemplateRows="1fr"
                    >
                      {slice.items?.map((item: any, itemIdx: number) => {
                        const isVisible = showLeftTabs
                          ? activeTab === itemIdx
                          : itemIdx === 0;
                        if (!showLeftTabs && itemIdx !== 0) return null;
                        const fragmentData =
                          item.header_fragment?.document?.data;
                        return (
                          <Box
                            key={itemIdx}
                            gridArea="1 / 1 / 2 / 2"
                            opacity={isVisible ? 1 : 0}
                            visibility={isVisible ? "visible" : "hidden"}
                            pointerEvents={isVisible ? "auto" : "none"}
                            display="flex"
                            w="100%"
                            transition="opacity 0.15s ease-in-out"
                          >
                            {(() => {
                              if (!fragmentData) {
                                return (
                                  <Box p="2em" maxW="300px">
                                    <Text color="red.500" fontWeight="bold">
                                      Missing Content!
                                    </Text>
                                    <Text
                                      mt="0.5em"
                                      fontSize="sm"
                                      color="grey-600"
                                    >
                                      The Header Fragment for this tab is either
                                      not linked in Prismic, or it is currently
                                      Unpublished.
                                    </Text>
                                  </Box>
                                );
                              }

                              const limitedCols = (
                                fragmentData.body || []
                              ).slice(0, 4);

                              const middleCols = limitedCols.filter(
                                (col: any) =>
                                  !col.__typename.includes("NavPromoColumn"),
                              );
                              const promoCols = limitedCols.filter((col: any) =>
                                col.__typename.includes("NavPromoColumn"),
                              );

                              return (
                                <Flex w="100%" flexGrow={1}>
                                  <Flex
                                    direction="column"
                                    justify="space-between"
                                    flexGrow={1}
                                  >
                                    <Flex>
                                      {middleCols.map(
                                        (
                                          col: any,
                                          colIdx: number,
                                          arr: any[],
                                        ) => {
                                          const nextCol = arr[colIdx + 1];
                                          let gap = "0px";
                                          if (nextCol) {
                                            const isCurrentIllus =
                                              col.__typename.includes(
                                                "IllustrationCardColumn",
                                              );
                                            const isNextIllus =
                                              nextCol.__typename.includes(
                                                "IllustrationCardColumn",
                                              );
                                            gap =
                                              isCurrentIllus && isNextIllus
                                                ? "1.5em"
                                                : "3em";
                                          }

                                          if (
                                            col.__typename.includes(
                                              "IllustrationCardColumn",
                                            )
                                          ) {
                                            return (
                                              <Box
                                                key={colIdx}
                                                w="14.0625em"
                                                h="18.75em"
                                                flexShrink={0}
                                                mr={gap}
                                              >
                                                <Link
                                                  href={col.primary.link?.url}
                                                  display="block"
                                                  _hover={{
                                                    textDecoration: "none",
                                                  }}
                                                  role="group"
                                                  h="100%"
                                                >
                                                  {(() => {
                                                    const isIllustration =
                                                      col.primary
                                                        .is_illustration !==
                                                      false;
                                                    return (
                                                      <Box
                                                        bg={
                                                          isIllustration
                                                            ? "secondary-stone-50"
                                                            : "transparent"
                                                        }
                                                        borderRadius="md"
                                                        p="0"
                                                        h="100%"
                                                        display="flex"
                                                        flexDirection="column"
                                                        justifyContent="flex-end"
                                                        transition="background-color 0.2s"
                                                        _groupHover={{
                                                          bg: isIllustration
                                                            ? "secondary-stone-100"
                                                            : "transparent",
                                                        }}
                                                        overflow="hidden"
                                                        position="relative"
                                                      >
                                                        {/* Image Container */}
                                                        <Box
                                                          flex="none"
                                                          display="flex"
                                                          alignItems="center"
                                                          justifyContent="center"
                                                          mb="0"
                                                          position="absolute"
                                                          top={0}
                                                          left={0}
                                                          w="100%"
                                                          h="100%"
                                                          zIndex={0}
                                                        >
                                                          {col.primary.graphics
                                                            ?.url && (
                                                            <img
                                                              src={
                                                                col.primary
                                                                  .graphics.url
                                                              }
                                                              alt={
                                                                col.primary
                                                                  .graphics.alt
                                                              }
                                                              style={{
                                                                maxWidth:
                                                                  "100%",
                                                                maxHeight:
                                                                  "100%",
                                                                width: "100%",
                                                                height: "100%",
                                                                objectFit:
                                                                  "cover",
                                                                mixBlendMode:
                                                                  "normal",
                                                              }}
                                                            />
                                                          )}
                                                        </Box>

                                                        {/* Gradient Overlay for Non-Illustration Cover Photos */}
                                                        {!isIllustration && (
                                                          <Box
                                                            position="absolute"
                                                            top={0}
                                                            left={0}
                                                            w="100%"
                                                            h="100%"
                                                            bg="linear-gradient(180deg, rgba(102, 102, 102, 0) 0%, #000000 100%)"
                                                            zIndex={1}
                                                            opacity={0.5}
                                                            transition="opacity 0.2s"
                                                            _groupHover={{
                                                              opacity: 0.6,
                                                            }}
                                                          />
                                                        )}

                                                        {/* Text Overlay */}
                                                        <Flex
                                                          p="1em"
                                                          justify="flex-start"
                                                          align="center"
                                                          color={
                                                            isIllustration
                                                              ? "accounting-green-800"
                                                              : "white"
                                                          }
                                                          fontWeight="semibold"
                                                          fontSize="1em"
                                                          position="relative"
                                                          zIndex={2}
                                                          mt="auto"
                                                        >
                                                          <Text textAlign="left">
                                                            {col.primary.label}
                                                          </Text>
                                                          <Box
                                                            as="span"
                                                            ml="0.5em"
                                                            opacity={0}
                                                            transform="translateX(-0.625em)"
                                                            transition="opacity 0.2s, transform 0.2s"
                                                            _groupHover={{
                                                              opacity: 1,
                                                              transform:
                                                                "translateX(0)",
                                                            }}
                                                          >
                                                            &rarr;
                                                          </Box>
                                                        </Flex>
                                                      </Box>
                                                    );
                                                  })()}
                                                </Link>
                                              </Box>
                                            );
                                          }
                                          if (
                                            col.__typename.includes(
                                              "LinksListColumn",
                                            )
                                          ) {
                                            return (
                                              <Box
                                                key={colIdx}
                                                w="14.0625em"
                                                flexShrink={0}
                                                mr={gap}
                                              >
                                                <Text
                                                  fontWeight="semibold"
                                                  mb="1.5em"
                                                  color="accounting-green-950"
                                                  fontSize="1em"
                                                  visibility={
                                                    col.primary.title
                                                      ? "visible"
                                                      : "hidden"
                                                  }
                                                  userSelect={
                                                    col.primary.title
                                                      ? "auto"
                                                      : "none"
                                                  }
                                                >
                                                  {col.primary.title ||
                                                    "\u00A0"}
                                                </Text>
                                                <Flex
                                                  direction="column"
                                                  gap="0.75em"
                                                >
                                                  {col.items?.map(
                                                    (
                                                      linkItem: any,
                                                      lIdx: number,
                                                    ) => (
                                                      <Link
                                                        key={lIdx}
                                                        href={
                                                          linkItem.link?.url
                                                        }
                                                        color="monochrome-800"
                                                        fontWeight="normal"
                                                        textDecoration="none !important"
                                                        _hover={{
                                                          color:
                                                            "accounting-green-950",
                                                          textDecoration:
                                                            "none",
                                                        }}
                                                      >
                                                        {linkItem.label}
                                                      </Link>
                                                    ),
                                                  )}
                                                </Flex>
                                                {col.primary.cta_label && (
                                                  <ShiButton
                                                    as={Link}
                                                    href={
                                                      col.primary.cta_link?.url
                                                    }
                                                    mt="2em"
                                                    variant="tertiary"
                                                    hasArrow={false}
                                                    fontSize="1em"
                                                  >
                                                    {col.primary.cta_label}
                                                  </ShiButton>
                                                )}
                                              </Box>
                                            );
                                          }
                                          return null;
                                        },
                                      )}
                                    </Flex>

                                    {/* Dropdown Footer (Below Middle Columns) */}
                                    {(() => {
                                      const footerLabel =
                                        fragmentData.footer_label ||
                                        slice.primary?.footer_label;
                                      const footerLink =
                                        fragmentData.footer_link?.url ||
                                        slice.primary?.footer_link?.url;
                                      return footerLabel && footerLink ? (
                                        <Box
                                          mt="auto"
                                          pt="1.5em"
                                          textAlign="left"
                                        >
                                          <ShiButton
                                            as={Link}
                                            href={footerLink}
                                            variant="tertiary"
                                            hasArrow={false}
                                            fontSize="1em"
                                            whiteSpace="nowrap"
                                          >
                                            {footerLabel}
                                          </ShiButton>
                                        </Box>
                                      ) : null;
                                    })()}
                                  </Flex>

                                  {/* Nav Promo Columns */}
                                  {promoCols.length > 0 && (
                                    <Flex
                                      ml={middleCols.length > 0 ? "4em" : 0}
                                    >
                                      {promoCols.map(
                                        (
                                          col: any,
                                          colIdx: number,
                                          arr: any[],
                                        ) => {
                                          const gap =
                                            colIdx < arr.length - 1
                                              ? "3em"
                                              : "0px";
                                          const validItems = col.items
                                            ? col.items.filter(
                                                (item: any) => item.label,
                                              )
                                            : [];
                                          const hasLinksOrTitle =
                                            col.primary.title ||
                                            validItems.length > 0;
                                          return (
                                            <Flex
                                              key={colIdx}
                                              w="14.0625em"
                                              flexShrink={0}
                                              mr={gap}
                                              direction="column"
                                              justify="space-between"
                                            >
                                              {hasLinksOrTitle && (
                                                <Box mb="2em">
                                                  {col.primary.title && (
                                                    <Text
                                                      fontWeight="semibold"
                                                      mb="1.5em"
                                                      color="accounting-green-950"
                                                      fontSize="1em"
                                                    >
                                                      {col.primary.title}
                                                    </Text>
                                                  )}
                                                  {validItems.length > 0 && (
                                                    <Flex
                                                      direction="column"
                                                      gap="0.75em"
                                                    >
                                                      {validItems.map(
                                                        (
                                                          linkItem: any,
                                                          lIdx: number,
                                                        ) => (
                                                          <Link
                                                            key={lIdx}
                                                            href={
                                                              linkItem.link?.url
                                                            }
                                                            color="monochrome-800"
                                                            fontWeight="normal"
                                                            textDecoration="none !important"
                                                            _hover={{
                                                              color:
                                                                "accounting-green-950",
                                                              textDecoration:
                                                                "none",
                                                            }}
                                                          >
                                                            {linkItem.label}
                                                          </Link>
                                                        ),
                                                      )}
                                                    </Flex>
                                                  )}
                                                </Box>
                                              )}

                                              <Link
                                                href={col.primary.link?.url}
                                                display="block"
                                                _hover={{
                                                  textDecoration: "none",
                                                }}
                                                role="group"
                                              >
                                                <Box>
                                                  <Box position="relative">
                                                    {col.primary.image?.url && (
                                                      <Box
                                                        position="relative"
                                                        bg="secondary-stone-50"
                                                        p="0"
                                                        borderRadius="md"
                                                        mb="1em"
                                                        w="14.0625em"
                                                        h="14.0625em"
                                                        display="flex"
                                                        alignItems="center"
                                                        justifyContent="center"
                                                        transition="background-color 0.2s"
                                                        _groupHover={{
                                                          bg: "secondary-stone-100",
                                                        }}
                                                        overflow="hidden"
                                                      >
                                                        <img
                                                          src={
                                                            col.primary.image
                                                              .url
                                                          }
                                                          alt={
                                                            col.primary.image
                                                              .alt
                                                          }
                                                          style={{
                                                            maxWidth: "100%",
                                                            maxHeight: "100%",
                                                            width: "100%",
                                                            height: "100%",
                                                            objectFit: "cover",
                                                            mixBlendMode:
                                                              "normal",
                                                          }}
                                                        />
                                                        {col.primary
                                                          .is_illustration ===
                                                          false && (
                                                          <Box
                                                            position="absolute"
                                                            top={0}
                                                            left={0}
                                                            w="100%"
                                                            h="100%"
                                                            bg="linear-gradient(180deg, rgba(102, 102, 102, 0) 0%, rgba(0, 0, 0, 0.8) 100%)"
                                                            zIndex={1}
                                                            opacity={0.5}
                                                            transition="opacity 0.2s"
                                                            _groupHover={{
                                                              opacity: 0.6,
                                                            }}
                                                          />
                                                        )}
                                                      </Box>
                                                    )}
                                                    <Box
                                                      position={
                                                        col.primary.image?.url
                                                          ? "absolute"
                                                          : "static"
                                                      }
                                                      bottom={0}
                                                      left={0}
                                                      w="100%"
                                                      p={
                                                        col.primary.image?.url
                                                          ? "1em"
                                                          : "0"
                                                      }
                                                      zIndex={2}
                                                    >
                                                      <Text
                                                        mt={
                                                          col.primary.image?.url
                                                            ? "0"
                                                            : "1em"
                                                        }
                                                        fontWeight="semibold"
                                                        fontSize="1em"
                                                        color={
                                                          col.primary.image
                                                            ?.url &&
                                                          col.primary
                                                            .is_illustration ===
                                                            false
                                                            ? "white"
                                                            : "accounting-green-950"
                                                        }
                                                        textAlign="left"
                                                      >
                                                        {col.primary.label}
                                                      </Text>
                                                    </Box>
                                                  </Box>
                                                  {(col.primary
                                                    .scraped_description ||
                                                    col.primary.description
                                                      ?.raw?.[0]?.text) && (
                                                    <Text
                                                      mt="1em"
                                                      fontSize="0.875em"
                                                      fontWeight="medium"
                                                      color="monochrome-600"
                                                      lineHeight="short"
                                                      noOfLines={3}
                                                      title={
                                                        col.primary
                                                          .scraped_description ||
                                                        col.primary.description
                                                          ?.raw?.[0]?.text
                                                      }
                                                      textAlign="left"
                                                      transition="color 0.2s"
                                                      _groupHover={{
                                                        color: "monochrome-800",
                                                      }}
                                                    >
                                                      {col.primary
                                                        .scraped_description ||
                                                        col.primary.description
                                                          ?.raw?.[0]?.text}
                                                    </Text>
                                                  )}
                                                </Box>
                                              </Link>
                                            </Flex>
                                          );
                                        },
                                      )}
                                    </Flex>
                                  )}
                                </Flex>
                              );
                            })()}
                          </Box>
                        );
                      })}
                    </Box>
                  </Flex>
                </Box>
              </Box>
            )}
          </Box>
        );
      })}
    </HStack>
  );
};
